import {
  CADENCE_BOUNDS, DEFAULT_DAILY_TEXTS, NEW_CADENCE, NEW_TEXT, PHASE_NAME_RE, callsFor, hasCalls, hasTexts, isText, repeats, stepsWords,
  strategiesFor, waitsFor, type CadenceFormValues, type CallValues, type PhaseValues,
} from "./cadence.js";
import { endOutcomeLabel } from "./cadence-end.js";

/**
 * The cadence builder's arithmetic (ADR-066/067): what a lead who never picks up actually gets
 * from a cadence, the templates a new one starts from, the words a wait reads as, and the edits
 * the builder makes to a phase. Pure, for `test/cadence-plan.test.ts`.
 *
 * `planCalls` plays the cadence forward the way the dialer runs it (`services/dialer/src/cadence.py`
 * in `platform`): a counted call moves the lead to the next call of its cycle after the wait
 * between calls, or to the next cycle after the longer wait; the cycle's shape is the phase's at
 * the moment the call ends; a mid-cycle call that could not go out before its deadline is dropped
 * and the lead starts the next cycle instead; nothing goes out outside the calling hours or past
 * the day's cap on one number; the cadence stops at its total, its expiry or its last phase. A call
 * made only once is made in its phase's first round, and a round that starts again in the same
 * phase skips it and the wait after it; a phase whose calls are all made once has one round
 * (ADR-103). Redials follow their call at once and each counts as a call (ADR-105). A step that
 * is a text (ADR-135) goes out when it is due inside the calling hours, takes no time, spends no
 * call and keeps no deadline; past the day's texts it is passed over, and the round goes on. A
 * call that only texts came before is the round's first call, and keeps no deadline either. It
 * assumes nobody ever answers, the campaign's default calling hours, every text allowed to
 * leave, and no other campaign calling the same number: a preview for reading a cadence, not a
 * promise about a lead.
 */

const DAY = 24 * 60;

export interface PlanOptions {
  /** Minutes after midnight the lead joins, on their clock. */
  joinMinute?: number;
  /** The campaign's calling hours on the lead's clock, minutes after midnight. */
  windowStart?: number;
  windowEnd?: number;
  /** How long an unanswered call rings before it ends, in minutes. */
  ringMinutes?: number;
  /** Never play further than this. */
  horizonDays?: number;
}

export const PLAN_DEFAULTS = {
  joinMinute: 9 * 60,
  windowStart: 8 * 60,
  windowEnd: 21 * 60,
  ringMinutes: 0.5,
  horizonDays: 365,
} as const;

export type Moved = "quiet_hours" | "daily_cap" | "replanned";

export interface PlannedCall {
  /** Minutes since midnight of the day the lead joined. */
  at: number;
  /** 1 on the day the lead joined. */
  day: number;
  /** Minutes after midnight on that day. */
  minute: number;
  phase: number;
  cycle: number;
  /** The call of its cycle, from 0. */
  step: number;
  /** 0 for the call itself, else which of its redials. */
  redial: number;
  strategy: string;
  /** Why it went out later than it was due, if it did. */
  moved: Moved | null;
}

/** A text a step sends (ADR-135 in platform), placed as a call is. */
export interface PlannedText {
  at: number;
  day: number;
  minute: number;
  phase: number;
  cycle: number;
  /** The step of its cycle, from 0. */
  step: number;
  moved: Moved | null;
}

export type PlanEnd = "total_attempts" | "expired" | "phases_over" | "horizon";

export interface PlannedPhase {
  name: string;
  fromDay: number;
  /** Null for an open-ended last phase. */
  toDay: number | null;
  calls: number;
  texts: number;
}

export interface Plan {
  /** The calls, redials among them. Never a text. */
  calls: PlannedCall[];
  /** The texts that go out. */
  texts: PlannedText[];
  /** Calls on each day, from the day the lead joined through `lastDay`. */
  perDay: number[];
  /** Texts on each day, the same days. */
  textsPerDay: number[];
  /** Text steps that were due and not sent: the lead had the day's texts already. */
  textsPassedOver: number;
  /** The last day the plan covers: the last call's day, or the day the cadence ends. */
  lastDay: number;
  endedBy: PlanEnd;
  phases: PlannedPhase[];
}

const whole = (n: unknown, fallback: number, [min, max]: readonly [number, number]) =>
  typeof n === "number" && Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : fallback;

const days = (text: string): number | null => {
  const t = text.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

export const dayOf = (at: number): number => Math.floor(at / DAY) + 1;

/** What a lead who never picks up gets, call by call. */
export function planCalls(v: CadenceFormValues, options: PlanOptions = {}): Plan {
  const o = { ...PLAN_DEFAULTS, ...options };
  const join = o.joinMinute;
  const total = whole(v.total_attempts, 1, CADENCE_BOUNDS.total_attempts);
  const cap = days(v.daily_calls_per_number);
  const textCap = days(v.daily_texts_per_lead ?? "") ?? DEFAULT_DAILY_TEXTS;
  const phaseEnds = v.phases.map((p) => {
    const d = days(p.ends_days);
    return d === null ? null : join + d * DAY;
  });
  const expire = days(v.expire_days);
  const lastEnd = phaseEnds.length ? (phaseEnds[phaseEnds.length - 1] ?? null) : join;
  const candidates: [number, PlanEnd][] = [[join + o.horizonDays * DAY, "horizon"]];
  if (expire !== null) candidates.push([join + expire * DAY, "expired"]);
  if (lastEnd !== null) candidates.push([lastEnd, "phases_over"]);
  const [ends, endsBy] = candidates.reduce((a, b) => (b[0] < a[0] ? b : a));

  const phaseAt = (t: number): number | null => {
    for (let i = 0; i < phaseEnds.length; i++) {
      const end = phaseEnds[i];
      if (end === null || end === undefined || t < end) return i;
    }
    return null;
  };
  const shape = (i: number) => {
    const p = v.phases[i]!;
    const attempts = whole(p.attempts, 1, CADENCE_BOUNDS.attempts);
    return {
      attempts,
      waits: waitsFor(attempts, p.between_minutes).map((m) => whole(m, 1, CADENCE_BOUNDS.between_minutes)),
      strategies: strategiesFor(attempts, p.did_strategy),
      calls: callsFor(attempts, p.calls ?? []),
    };
  };
  /** The call a round makes at `step` or the first after it: a round that starts again in its
   *  phase passes over the calls made once. */
  const made = (s: ReturnType<typeof shape>, step: number, repeat: boolean): number | null => {
    for (let k = Math.max(0, step); k < s.attempts; k++) if (!(repeat && s.calls[k]?.only_once)) return k;
    return null;
  };
  const inHours = (t: number) => {
    const m = ((t % DAY) + DAY) % DAY;
    return m >= o.windowStart && m < o.windowEnd;
  };
  const nextOpen = (t: number) => {
    const start = Math.floor(t / DAY) * DAY;
    const m = t - start;
    return m < o.windowStart ? start + o.windowStart : start + DAY + o.windowStart;
  };

  const calls: PlannedCall[] = [];
  const texts: PlannedText[] = [];
  const count = new Map<number, number>();
  const textCount = new Map<number, number>();
  let textsPassedOver = 0;
  /** The phases earlier rounds were made in, and the phase of the round being made. */
  const roundsIn = new Set<number>();
  let roundPhase: number | null = null;
  let cycle = 1;
  let step = 0;
  let due = join + whole(v.phases[0]?.initial_delay_minutes, 0, CADENCE_BOUNDS.initial_delay_minutes);
  let notAfter: number | null = null;
  let endedBy: PlanEnd = endsBy;
  let endAt = ends;

  /** Where the lead goes from a step that ended at `ended`: the round's next step after its
   *  wait, or the next round. False when the cadence is over there. */
  const moveOn = (ended: number): boolean => {
    if (ended >= ends) {
      endAt = ends;
      return false;
    }
    const next = phaseAt(ended);
    if (next === null) {
      endedBy = "phases_over";
      endAt = ended;
      return false;
    }
    const ns = shape(next);
    const p = v.phases[next]!;
    const after = made(ns, step + 1, roundsIn.has(next));
    if (after !== null) {
      due = ended + (ns.waits[Math.min(step, ns.waits.length - 1)] ?? 1);
      step = after;
      // Only a call keeps a deadline, and only one that another call of the round came before:
      // a text is as good in the morning, and a call after texts alone is the round's first.
      const repeat = roundsIn.has(next);
      const second = ns.calls.slice(0, after).some((c) => !isText(c) && !(repeat && c.only_once));
      const late = !second || isText(ns.calls[after]!) ? null : days(p.not_after_minutes);
      notAfter = late === null ? null : due + late;
      return true;
    }
    if (roundPhase !== null) roundsIn.add(roundPhase);
    roundPhase = null;
    due = ended + whole(p.after_cycle_minutes, DAY, CADENCE_BOUNDS.wait_minutes);
    // A phase whose calls are all made once has one round: the next waits for the next phase.
    if (repeats(p).kind === "once" && phaseAt(due) === next) {
      const phaseEnd = phaseEnds[next];
      if (phaseEnd === null || phaseEnd === undefined) {
        endedBy = "phases_over";
        endAt = ended;
        return false;
      }
      due = phaseEnd;
    }
    cycle += 1;
    step = 0;
    notAfter = null;
    return true;
  };
  /** Whether the step a round makes at `step` or after it, at `t`, is a text. */
  const textAt = (t: number, from: number): boolean => {
    const i = phaseAt(t);
    if (i === null) return false;
    const s = shape(i);
    const k = made(s, from, roundsIn.has(i)) ?? from;
    return Boolean(s.calls[k] && isText(s.calls[k]!));
  };

  // A step an iteration: every call, and every text due, sent or passed over. A year of a text
  // an hour is under five thousand of them.
  for (let guard = 0; guard < 20_000 && v.phases.length > 0; guard++) {
    // The gate: the calling hours, then the day's cap, the way the claim reads them. The day's
    // calls hold a call back, never a text.
    let at = due;
    let moved: Moved | null = null;
    for (let g = 0; g < 1000; g++) {
      if (!inHours(at)) {
        at = nextOpen(at);
        moved ??= "quiet_hours";
        continue;
      }
      if (cap !== null && (count.get(dayOf(at)) ?? 0) >= cap && !textAt(at, step)) {
        at = Math.floor(at / DAY) * DAY + DAY + o.windowStart;
        moved = "daily_cap";
        continue;
      }
      break;
    }
    // A mid-cycle call past its deadline is not made late: the next cycle starts instead.
    if (step > 0 && notAfter !== null && at > notAfter) {
      if (roundPhase !== null) roundsIn.add(roundPhase);
      roundPhase = null;
      cycle += 1;
      step = 0;
      notAfter = null;
      moved = "replanned";
    }
    if (at >= ends) {
      endAt = ends;
      break;
    }
    const phase = phaseAt(at);
    if (phase === null) {
      endedBy = "phases_over";
      endAt = at;
      break;
    }
    const s = shape(phase);
    const repeat = roundsIn.has(phase);
    step = made(s, step, repeat) ?? step;
    roundPhase ??= phase;
    if (s.calls[step] && isText(s.calls[step]!)) {
      // A text: sent now, or passed over when the lead had the day's texts. Either way the
      // round goes on from it, and it took no time.
      if ((textCount.get(dayOf(at)) ?? 0) >= textCap) {
        textsPassedOver += 1;
      } else {
        texts.push({ at, day: dayOf(at), minute: at - Math.floor(at / DAY) * DAY, phase, cycle, step, moved });
        textCount.set(dayOf(at), (textCount.get(dayOf(at)) ?? 0) + 1);
      }
      if (!moveOn(at)) break;
      continue;
    }
    calls.push({
      at,
      day: dayOf(at),
      minute: at - Math.floor(at / DAY) * DAY,
      phase,
      cycle,
      step,
      redial: 0,
      strategy: s.strategies[Math.min(step, s.strategies.length - 1)] ?? "branded",
      moved,
    });
    count.set(dayOf(at), (count.get(dayOf(at)) ?? 0) + 1);
    if (calls.length >= total) {
      endedBy = "total_attempts";
      endAt = at;
      break;
    }
    let ended = at + o.ringMinutes;
    // The call's redials, at once, while the calling hours and the day's cap allow: the first
    // the gate refuses ends them (ADR-105). Each one is a call.
    for (const [r, redial] of (s.calls[step]?.redials ?? []).entries()) {
      if (!inHours(ended) || (cap !== null && (count.get(dayOf(ended)) ?? 0) >= cap)) break;
      calls.push({
        at: ended,
        day: dayOf(ended),
        minute: ended - Math.floor(ended / DAY) * DAY,
        phase,
        cycle,
        step,
        redial: r + 1,
        strategy: redial.did_strategy,
        moved: null,
      });
      count.set(dayOf(ended), (count.get(dayOf(ended)) ?? 0) + 1);
      ended += o.ringMinutes;
      if (calls.length >= total) break;
    }
    if (calls.length >= total) {
      endedBy = "total_attempts";
      endAt = ended;
      break;
    }
    if (!moveOn(ended)) break;
  }

  const lastCall = calls[calls.length - 1];
  const lastDay = endedBy === "total_attempts" && lastCall ? lastCall.day : dayOf(Math.max(join, endAt - 1));
  const perDay = Array.from({ length: Math.max(1, lastDay) }, (_, i) => count.get(i + 1) ?? 0);
  const textsPerDay = Array.from({ length: Math.max(1, lastDay) }, (_, i) => textCount.get(i + 1) ?? 0);
  let from = 1;
  const phases = v.phases.map((p, i) => {
    const to = days(p.ends_days);
    const out: PlannedPhase = {
      name: p.name,
      fromDay: from,
      toDay: to,
      calls: calls.filter((c) => c.phase === i).length,
      texts: texts.filter((t) => t.phase === i).length,
    };
    from = (to ?? from) + 1;
    return out;
  });
  return { calls, texts, perDay, textsPerDay, textsPassedOver, lastDay, endedBy, phases };
}

// --- what the preview says -------------------------------------------------------------------------

export interface PlanNote {
  tone: "info" | "warning";
  text: string;
}

/** A phase as the admin reads it: "“followup” (days 15–90)". */
export function phaseRange(p: { name: string; fromDay: number; toDay: number | null }): string {
  const days = p.toDay === null ? `day ${p.fromDay} on` : p.fromDay === p.toDay ? `day ${p.fromDay}` : `days ${p.fromDay}–${p.toDay}`;
  return `“${p.name}” (${days})`;
}

/** What is worth knowing about a cadence that its numbers alone do not say. */
export function planNotes(v: CadenceFormValues, plan: Plan, uncapped?: Plan): PlanNote[] {
  const out: PlanNote[] = [];
  const total = plan.calls.length;
  if (plan.endedBy === "total_attempts") {
    const unreached = plan.phases.filter((p, i) => i > 0 && p.calls === 0);
    if (unreached.length) {
      out.push({
        tone: "warning",
        text: `All ${total} calls are made by day ${plan.lastDay}, so ${unreached.map(phaseRange).join(" and ")} never ${unreached.length === 1 ? "gets" : "get"} a call. Allow more calls in all, or space the earlier calls out.`,
      });
    }
  }
  const cap = days(v.daily_calls_per_number);
  if (cap !== null && uncapped) {
    const firstWeek = uncapped.perDay.slice(0, 7);
    const most = Math.max(0, ...firstWeek);
    if (most > cap) {
      out.push({
        tone: "info",
        text: `The daily cap holds the busiest days to ${cap} call${cap === 1 ? "" : "s"}; without it the first week would reach ${most} a day.`,
      });
    }
  }
  const expire = days(v.expire_days);
  const last = plan.phases[plan.phases.length - 1];
  if (expire !== null && last && (last.toDay === null || last.toDay > expire) && plan.endedBy !== "total_attempts") {
    const cut = plan.phases.filter((p) => p.fromDay > expire);
    if (cut.length) {
      out.push({
        tone: "warning",
        text: `The cadence stops on day ${expire}, before ${cut.map(phaseRange).join(" and ")} ${cut.length === 1 ? "starts" : "start"}.`,
      });
    } else if (last.toDay !== null) {
      out.push({ tone: "info", text: `The cadence stops on day ${expire}, before ${phaseRange(last)} is over.` });
    }
  }
  if (expire === null && last?.toDay === null && plan.endedBy === "total_attempts") {
    out.push({ tone: "info", text: `No end day: a lead is called until all ${total} calls are made.` });
  }
  if (plan.textsPassedOver > 0) {
    const textCap = days(v.daily_texts_per_lead ?? "") ?? DEFAULT_DAILY_TEXTS;
    const n = plan.textsPassedOver;
    out.push({
      tone: "info",
      text: `${n} time${n === 1 ? "" : "s"} a text is due after the day's ${textCap === 1 ? "one text" : `${textCap} texts`} and is passed over. What follows it is still made.`,
    });
  }
  v.phases.forEach((p, i) => {
    const r = repeats(p);
    const name = phaseRange(plan.phases[i] ?? { name: p.name, fromDay: 1, toDay: null });
    if (r.kind === "some") {
      const steps = callsFor(p.attempts, p.calls);
      const once = steps.flatMap((c, k) => (c.only_once ? [k] : []));
      out.push({
        tone: "info",
        text: `In ${name}, ${stepsWords(steps, once)} ${once.length === 1 ? "is" : "are"} made once: every round after the first starts with ${stepsWords(steps, r.calls.slice(0, 1))}.`,
      });
    } else if (r.kind === "once") {
      const next = plan.phases[i + 1];
      out.push({
        tone: "info",
        text: next
          ? `${name[0]!.toUpperCase()}${name.slice(1)} runs one round, then the lead waits for day ${next.fromDay}.`
          : `${name[0]!.toUpperCase()}${name.slice(1)} runs one round, and the cadence stops after it.`,
      });
    }
  });
  const replanned = plan.calls.filter((c) => c.moved === "replanned").length;
  if (replanned > 0) {
    out.push({
      tone: "info",
      text: `${replanned} time${replanned === 1 ? "" : "s"} a second call runs past its deadline (calling hours or the daily cap) and the lead starts the next cycle instead.`,
    });
  }
  return out;
}

/** How far up each call of a day sits on its line, in rows: calls minutes apart would sit on one
 *  another, so each goes above the ones just before it. */
export function dayStacks(minutes: readonly number[]): number[] {
  return minutes.map((m, i) => minutes.slice(0, i).filter((p) => m - p < 12).length);
}

/** The rise of one row in pixels, and the room the tallest stack needs above the line: a call
 *  with its redials is a short stack and gets full rows, a day of calls minutes apart is a tall
 *  one and is squeezed, so the line never grows past a few rows' worth. */
export function stackRoom(stacks: readonly number[]): { step: number; room: number } {
  const tallest = Math.max(0, ...stacks);
  const step = tallest > 8 ? Math.max(2, Math.floor(56 / tallest)) : 7;
  return { step, room: tallest * step };
}

/** The busiest day's calls, the day it happens first. */
export function busiestDay(plan: Plan): { day: number; calls: number } {
  let best = { day: 1, calls: 0 };
  plan.perDay.forEach((n, i) => {
    if (n > best.calls) best = { day: i + 1, calls: n };
  });
  return best;
}

// --- words --------------------------------------------------------------------------------------------

/** "right away", "1 min", "90 min", "4 hours", "1 day", "3 days", "1 week", "1 h 30 min". */
export function waitWords(minutes: number): string {
  if (!Number.isFinite(minutes)) return "—";
  if (minutes <= 0) return "right away";
  if (minutes % (7 * DAY) === 0) {
    const w = minutes / (7 * DAY);
    return `${w} week${w === 1 ? "" : "s"}`;
  }
  if (minutes % DAY === 0) {
    const d = minutes / DAY;
    return `${d} day${d === 1 ? "" : "s"}`;
  }
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  if (minutes < 120) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** "9:00 am", "1:01 pm", "noon". */
export function clockWords(minute: number): string {
  const m = Math.floor(((minute % DAY) + DAY) % DAY);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 12 && mm === 0) return "noon";
  const suffix = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${suffix}`;
}

/** The day a phase starts on, counted from 1 on the day the lead joined. */
export function phaseFromDay(values: CadenceFormValues, index: number): number {
  let from = 1;
  for (let i = 0; i < index; i++) from = (days(values.phases[i]?.ends_days ?? "") ?? from - 1) + 1;
  return from;
}

/** The days a phase covers, for its card: "Days 1–14", "Days 15–90", "From day 91". */
export function phaseDays(values: CadenceFormValues, index: number): string {
  const from = phaseFromDay(values, index);
  const to = days(values.phases[index]?.ends_days ?? "");
  if (to === null) return from === 1 ? "Every day" : `From day ${from}`;
  return from === to ? `Day ${from}` : `Days ${from}–${to}`;
}

// --- presets ------------------------------------------------------------------------------------------

/** The waits a picker offers, in minutes, by what the wait is for. */
export const WAIT_PRESETS = {
  between: [0, 1, 2, 5, 10, 15, 30, 60, 120],
  cycle: [60, 120, 240, 360, 480, DAY, 2 * DAY, 3 * DAY, 7 * DAY],
  firstCall: [0, 1, 5, 15, 30, 60, 240, DAY],
  lateCall: [30, 60, 120, 240, 480, DAY],
  technical: [1, 2, 5, 10, 15, 30, 60],
  notWorked: [5, 10, 15, 30, 60, 120],
  callback: [15, 30, 60, 120, 240, DAY],
  callbackKept: [5, 15, 30, 60, 120],
  callbackWindow: [30, 60, 90, 120, 180, 240],
} as const;

export const DAY_PRESETS = [3, 7, 10, 14, 21, 30, 45, 60, 90, 120, 180, 365] as const;

/** The days a phase may end on: after the phase before it, before the phase after it. */
export function phaseEndBounds(values: CadenceFormValues, index: number): { min: number; max: number; open: boolean } {
  const before = index > 0 ? days(values.phases[index - 1]?.ends_days ?? "") ?? 0 : 0;
  const after = index < values.phases.length - 1 ? days(values.phases[index + 1]?.ends_days ?? "") : null;
  return {
    min: before + 1,
    max: after === null ? CADENCE_BOUNDS.expire_days[1] : after - 1,
    open: index === values.phases.length - 1,
  };
}

// --- edits --------------------------------------------------------------------------------------------

/** A new phase after the last. An open-ended last phase gets an end two weeks on, so only the new
 *  one is open-ended; otherwise the new one runs a month past the last. */
export function withPhase(values: CadenceFormValues): CadenceFormValues {
  if (values.phases.length >= CADENCE_BOUNDS.phases[1]) return values;
  const phases = values.phases.map((p) => ({ ...p }));
  const last = phases[phases.length - 1];
  const taken = new Set(phases.map((p) => p.name));
  let n = phases.length + 1;
  let name = `phase_${n}`;
  while (taken.has(name)) name = `phase_${++n}`;
  let ends = "";
  if (last) {
    const lastEnd = days(last.ends_days);
    if (lastEnd === null) {
      const start = phases.length > 1 ? (days(phases[phases.length - 2]!.ends_days) ?? 0) : 0;
      last.ends_days = String(Math.min(CADENCE_BOUNDS.expire_days[1] - 1, start + 14));
    } else {
      ends = String(Math.min(CADENCE_BOUNDS.expire_days[1], lastEnd + 30));
    }
  }
  phases.push({
    name, ends_days: ends, priority_band: 100, age_decay_per_day: 0, not_after_minutes: "",
    attempts: 1, between_minutes: [], after_cycle_minutes: 3 * DAY, initial_delay_minutes: 0,
    did_strategy: ["branded"], calls: callsFor(1, []),
  });
  return { ...values, phases };
}

/** The cadence without phase `index`; a cadence keeps one phase. */
export function withoutPhase(values: CadenceFormValues, index: number): CadenceFormValues {
  if (values.phases.length <= 1) return values;
  return { ...values, phases: values.phases.filter((_, i) => i !== index) };
}

/** Whether a phase name is one the server takes, and unique in the cadence. */
export function phaseNameProblem(values: CadenceFormValues, index: number, name: string): string | null {
  const n = name.trim();
  if (!PHASE_NAME_RE.test(n)) return "Lowercase letters, digits and underscores, starting with a letter, up to 24";
  if (values.phases.some((p, i) => i !== index && p.name === n)) return "Another phase has this name";
  return null;
}

/** A slug from a name: lowercase words joined by underscores, starting with a letter, unique
 *  among `taken`. */
export function slugFrom(name: string, taken: Iterable<string> = []): string {
  let base = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (base && !/^[a-z]/.test(base)) base = `c_${base}`;
  base = base.slice(0, 72).replace(/_+$/g, "");
  if (base.length < 2) base = "cadence";
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let i = 2; ; i++) {
    const next = `${base}_${i}`;
    if (!used.has(next)) return next;
  }
}

// --- templates ----------------------------------------------------------------------------------------

export interface CadenceTemplate {
  id: string;
  name: string;
  description: string;
  values: CadenceFormValues;
}

const phase = (p: Partial<PhaseValues> & Pick<PhaseValues, "name">): PhaseValues => ({
  ends_days: "", priority_band: 400, age_decay_per_day: 0, not_after_minutes: "", attempts: 1,
  between_minutes: [], after_cycle_minutes: 240, initial_delay_minutes: 0, did_strategy: ["branded"],
  ...p,
  calls: callsFor(p.attempts ?? 1, p.calls ?? []),
});

const copyPhase = (p: PhaseValues): PhaseValues => ({
  ...p, between_minutes: [...p.between_minutes], did_strategy: [...p.did_strategy], calls: callsFor(p.attempts, p.calls),
});

const base = (over: Partial<CadenceFormValues>): CadenceFormValues => ({
  ...NEW_CADENCE,
  phases: NEW_CADENCE.phases.map(copyPhase),
  ...over,
});

/** The words the texting shape starts with: a template is one the server takes as it stands, so
 *  its text cannot be empty. They are an example for an admin to rewrite, and use both kinds of
 *  field: one with fallback words, one without. */
export const STARTER_TEXT =
  "Hi {{lead.first_name|there}}, thanks for reaching out. We are calling you now. You can also call or text us at {{campaign.callback_number}}.";

/** Where a new cadence starts. Every number is the admin's to change; these are shapes the floor
 *  already talks about. */
export const TEMPLATES: readonly CadenceTemplate[] = [
  {
    id: "speed_to_lead",
    name: "Speed to lead",
    description: "Two calls a minute apart, again every 4 hours for two weeks, then one call every 3 days.",
    values: base({ name: "Speed to lead" }),
  },
  {
    id: "triple_dial",
    name: "Triple dial",
    description: "Three calls back to back, the third from a local number, every 4 hours for a week; then one a day.",
    values: base({
      name: "Triple dial",
      phases: [
        phase({
          name: "first_week", ends_days: "7", priority_band: 400, age_decay_per_day: 10, not_after_minutes: "120",
          attempts: 3, between_minutes: [0, 0], after_cycle_minutes: 240,
          did_strategy: ["branded", "same_as_last", "unbranded"],
        }),
        phase({ name: "daily", ends_days: "30", priority_band: 100, after_cycle_minutes: DAY }),
      ],
      total_attempts: 30,
      expire_days: "30",
      daily_calls_per_number: "3",
    }),
  },
  {
    id: "steady",
    name: "Every few hours",
    description: "One call every 4 hours for a week, then one a day for three weeks.",
    values: base({
      name: "Every few hours",
      phases: [
        phase({ name: "first_week", ends_days: "7", priority_band: 400, age_decay_per_day: 10, after_cycle_minutes: 240 }),
        phase({ name: "daily", ends_days: "30", priority_band: 100, after_cycle_minutes: DAY }),
      ],
      total_attempts: 30,
      expire_days: "30",
      daily_calls_per_number: "3",
    }),
  },
  {
    id: "gentle",
    name: "Once a day",
    description: "One call a day for two weeks, then one a week for six weeks.",
    values: base({
      name: "Once a day",
      phases: [
        phase({ name: "daily", ends_days: "14", priority_band: 300, age_decay_per_day: 5, after_cycle_minutes: DAY }),
        phase({ name: "weekly", ends_days: "56", priority_band: 100, after_cycle_minutes: 7 * DAY }),
      ],
      total_attempts: 20,
      expire_days: "56",
      daily_calls_per_number: "2",
    }),
  },
  {
    id: "text_then_calls",
    name: "Text, then calls",
    description: "A text when the lead joins, once, then two calls a minute apart every 4 hours for two weeks. The text's words are yours to change.",
    values: base({
      name: "Text, then calls",
      phases: [
        phase({
          name: "initial", ends_days: "14", priority_band: 400, age_decay_per_day: 10, not_after_minutes: "120",
          attempts: 3, between_minutes: [1, 1], after_cycle_minutes: 240,
          did_strategy: ["branded", "branded", "same_as_last"],
          calls: [{ ...NEW_TEXT, only_once: true, text: STARTER_TEXT }, ...callsFor(2, [])],
        }),
        copyPhase(NEW_CADENCE.phases[1]!),
      ],
    }),
  },
];

/** A deep copy of a template's values, so edits never reach the shared constant. */
export function fromTemplate(t: CadenceTemplate): CadenceFormValues {
  return { ...t.values, phases: t.values.phases.map(copyPhase) };
}

// --- "In short" ---------------------------------------------------------------------------------------

export interface SummaryLine {
  /** The days the line is about: "Days 1–14". */
  label: string;
  text: string;
}

const calls = (n: number) => `${n} call${n === 1 ? "" : "s"}`;

/** The steps of one round in words: "call 1 right away with 2 redials, then call 2 a minute
 *  later", "text 1, then call 2 1 min later". `which` is the steps the round makes, from 0. */
function roundWords(p: PhaseValues, which: readonly number[]): string {
  const waits = waitsFor(p.attempts, p.between_minutes);
  const list = callsFor(p.attempts, p.calls);
  return which
    .map((k, i) => {
      const call: CallValues | undefined = list[k];
      const text = call !== undefined && isText(call);
      const n = text ? 0 : (call?.redials.length ?? 0);
      const extra = n ? ` with ${n} redial${n === 1 ? "" : "s"}` : "";
      const name = `${text ? "text" : "call"} ${k + 1}`;
      if (i === 0) return `${name}${extra}`;
      const wait = waits[which[i - 1]!] ?? 1;
      return `${name} ${wait <= 0 ? "right after" : `${waitWords(wait)} later`}${extra}`;
    })
    .join(", then ");
}

/** The whole cadence in a few lines, one per phase, for the top of the page. */
export function cadenceSummary(
  v: CadenceFormValues,
  /** The name of the outcome it sets when it runs out, from the catalog; the slug's words without. */
  endLabel?: string,
): { lines: SummaryLine[]; limits: string } {
  const lines = v.phases.map((p, i) => {
    const label = phaseDays(v, i);
    const r = repeats(p);
    const every = waitWords(p.after_cycle_minutes);
    // ADR-156: the phase's rounds start in the lead's calling window, when they have one.
    const inWindow = p.prefer_window ? " Rounds start in the lead's calling window." : "";
    const all = Array.from({ length: Math.max(1, p.attempts) }, (_, k) => k);
    const capital = (s: string) => s[0]!.toUpperCase() + s.slice(1);
    if (r.kind === "once") return { label, text: `${capital(roundWords(p, all))}. Once.${inWindow}` };
    if (r.kind === "some") {
      return {
        label,
        text: `${capital(roundWords(p, all))}. Then ${roundWords(p, r.calls)}, again every ${every}.${inWindow}`,
      };
    }
    const plain = callsFor(p.attempts, p.calls).every((c) => !isText(c) && c.redials.length === 0);
    if (plain) {
      const waits = waitsFor(p.attempts, p.between_minutes);
      const gap = p.attempts > 1 ? ((waits[0] ?? 1) <= 0 ? " back to back" : ` ${waitWords(waits[0] ?? 1)} apart`) : "";
      return { label, text: `${capital(calls(p.attempts))}${gap}, again every ${every}.${inWindow}` };
    }
    return { label, text: `${capital(roundWords(p, all))}, again every ${every}.${inWindow}` };
  });
  const expire = days(v.expire_days);
  const cap = days(v.daily_calls_per_number);
  // A cadence with no call in it has no calls to count: its days end it.
  const dials = hasCalls(v);
  const stops = dials
    ? expire === null ? `Stops after ${calls(v.total_attempts)}.` : `Stops after ${calls(v.total_attempts)} or on day ${expire}.`
    : expire === null ? "No end day." : `Stops on day ${expire}.`;
  const most = !dials ? "" : cap === null ? "No cap on calls to one number a day." : `At most ${calls(cap)} to one number a day.`;
  const textCap = days(v.daily_texts_per_lead ?? "") ?? DEFAULT_DAILY_TEXTS;
  const mostTexts = !hasTexts(v) ? "" : `At most ${textCap} text${textCap === 1 ? "" : "s"} to one number a day.`;
  const redialed = v.phases.some((p) => callsFor(p.attempts, p.calls).some((c) => c.redials.length > 0));
  // What it sets when it runs out (ADR-155).
  const end = (v.end_outcome ?? "") === "" ? "" : `When it runs out, sets ${endLabel ?? endOutcomeLabel(v.end_outcome)}.`;
  // How long a callback is worked with the phase's own calls (ACL-661); nothing said for none.
  const worked = v.callback_window_minutes === null || v.callback_window_minutes === undefined
    ? ""
    : `A callback is worked for ${waitWords(v.callback_window_minutes)} after its time.`;
  return {
    lines,
    limits: `${[stops, most, mostTexts].filter(Boolean).join(" ")}${redialed ? " Every redial counts as a call." : ""}${worked ? ` ${worked}` : ""}${end ? ` ${end}` : ""}`,
  };
}
